import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Table, Tag, Button, Input, Space, Tooltip, Switch, Popconfirm,
  Modal, Form, InputNumber, message as antMessage, Select, DatePicker,
  Popover, Dropdown, Checkbox,
} from "antd";
import {
  EyeOutlined,
  MailOutlined,
  UserOutlined,
  PhoneOutlined,
  ClockCircleOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  DatabaseOutlined,
  InfoCircleOutlined,
  MoreOutlined,
  DeleteOutlined,
  DownloadOutlined,
  PlusOutlined,
  UserAddOutlined,
  CopyOutlined,
  DollarOutlined,
  ApartmentOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { all_routes } from "../../../router/all_routes";
import { showToast } from "../../../../core/data/redux/slices/ToastSlice";
import api from "../../../../core/axios/axiosInstance";
import { useDebounce } from "../../../../core/hooks/useDebounce";
import {
  setCompaniesPage,
  getCompaniesPage,
  clearCompaniesCache,
  setSearchQuery,
  setCurrentPage,
  setPageSize,
  setLoading,
  setError,
} from "../../../../core/data/redux/slices/adminCompanySlice";
import {
  cancelUserSubscription,
  updateUserTrialPeriod,
  pauseUserAccess,
  resumeUserAccess,
} from "../../../../core/data/redux/slices/PlanSlice";
import { fetchDevices } from "../../../../core/data/redux/slices/AllDeviceSlice";

const DEFAULT_FILTERS = {
  accountStatus: "active",
  extStatus: "all",
  device: "all",
  plan: "all",
  paused: "all",
  dateRange: null,
};

const ManageCompanies = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state — must come before any useState that uses these values
  const { companies, cachedPages, pagination, searchQuery, loading } =
    useSelector((state) => state.adminCompany);
  const { devices } = useSelector((state) => state.devices);

  // Local state
  const [localSearch, setLocalSearch] = useState(searchQuery);
  const debouncedSearch = useDebounce(localSearch, 500);
  const [loadingToggles, setLoadingToggles] = useState({});
  const [planActionLoading, setPlanActionLoading] = useState({});
  const [trialModal, setTrialModal] = useState(false);
  const [trialTarget, setTrialTarget] = useState(null);
  const [trialSaving, setTrialSaving] = useState(false);
  const [trialForm] = Form.useForm();
  const [trialPreviewDays, setTrialPreviewDays] = useState(7);
  const [backupModal, setBackupModal] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const [mongodumpLoading, setMongodumpLoading] = useState(false);
  const [collections, setCollections] = useState([]);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [colDownloading, setColDownloading] = useState({});
  const [s3Backups, setS3Backups] = useState([]);
  const [s3Loading, setS3Loading] = useState(false);
  const [s3Uploading, setS3Uploading] = useState(false);

  // ── Superadmin user-management modals ────────────────────────────────────
  const [createAdminModal, setCreateAdminModal] = useState(false);
  const [createAdminForm] = Form.useForm();
  const [createAdminLoading, setCreateAdminLoading] = useState(false);

  // ── Adjust Balance modal ──────────────────────────────────────────────────
  const [balanceModal, setBalanceModal] = useState(false);
  const [balanceTarget, setBalanceTarget] = useState(null); // the company admin record
  const [balanceForm] = Form.useForm();
  const [balanceSaving, setBalanceSaving] = useState(false);

  // ── Assigned Extensions modal ─────────────────────────────────────────────
  const [extModal, setExtModal]         = useState(false);
  const [extTarget, setExtTarget]       = useState(null); // company admin record
  const [extList, setExtList]           = useState([]);   // current extensions
  const [extLoading, setExtLoading]     = useState(false);
  const [extSaving, setExtSaving]       = useState(false);
  const [extForm] = Form.useForm();

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // All filters in one object — changes trigger server-side re-fetch
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  // Ref so async fetch always reads the latest filters
  const filtersRef = useRef(filters);
  useEffect(() => { filtersRef.current = filters; }, [filters]);

  // ─── Filter change helper ──────────────────────────────────────────────────
  const handleFilterChange = (key, value) => {
    const newFilters = { ...filtersRef.current, [key]: value };
    setFilters(newFilters);
    dispatch(clearCompaniesCache());
    // Reset to page 1; if already on 1, fetch directly since Redux won't detect change
    if (pagination.current !== 1) {
      dispatch(setCurrentPage(1));
    } else {
      fetchCompanyAdmins(1, pagination.pageSize, searchQuery, newFilters, true);
    }
  };

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    dispatch(clearCompaniesCache());
    if (pagination.current !== 1) {
      dispatch(setCurrentPage(1));
    } else {
      fetchCompanyAdmins(1, pagination.pageSize, searchQuery, DEFAULT_FILTERS, true);
    }
  };

  // ─── Backup helpers ────────────────────────────────────────────────────────
  const openBackupModal = async () => {
    setBackupModal(true);
    setCollectionsLoading(true);
    fetchS3Backups(); // load S3 backups in parallel
    try {
      const res = await api.get("/superAdmin/backup/collections");
      setCollections(res.data.collections || []);
    } catch {
      antMessage.error("Failed to load collections");
    } finally {
      setCollectionsLoading(false);
    }
  };

  const triggerDownload = (blob, filename, type = "application/octet-stream") => {
    const url = window.URL.createObjectURL(new Blob([blob], { type }));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadAll = async () => {
    setBackupLoading(true);
    antMessage.loading({ content: "Preparing full backup...", key: "backup", duration: 0 });
    try {
      const response = await api.get("/superAdmin/backup/download", { responseType: "blob" });
      const dateStr = new Date().toISOString().slice(0, 10);
      triggerDownload(response.data, `voycell-backup-${dateStr}.zip`, "application/zip");
      antMessage.success({ content: "Full backup downloaded", key: "backup", duration: 3 });
    } catch {
      antMessage.error({ content: "Backup failed. Please try again.", key: "backup", duration: 3 });
    } finally {
      setBackupLoading(false);
    }
  };

  const handleDownloadMongodump = async () => {
    setMongodumpLoading(true);
    antMessage.loading({ content: "Running mongodump, this may take a moment...", key: "mongodump", duration: 0 });
    try {
      const response = await api.get("/superAdmin/backup/mongodump", {
        responseType: "blob",
        timeout: 10 * 60 * 1000,
      });
      const dateStr = new Date().toISOString().slice(0, 10);
      triggerDownload(response.data, `voycell-mongodump-${dateStr}.gz`, "application/gzip");
      antMessage.success({ content: "mongodump downloaded successfully", key: "mongodump", duration: 3 });
    } catch {
      antMessage.error({ content: "mongodump failed. Check server logs.", key: "mongodump", duration: 4 });
    } finally {
      setMongodumpLoading(false);
    }
  };

  const handleDownloadCollection = async (name) => {
    setColDownloading((prev) => ({ ...prev, [name]: true }));
    try {
      const response = await api.get(`/superAdmin/backup/collection/${name}`, { responseType: "blob" });
      const dateStr = new Date().toISOString().slice(0, 10);
      triggerDownload(response.data, `${name}-${dateStr}.json`, "application/json");
      antMessage.success(`${name}.json downloaded`);
    } catch {
      antMessage.error(`Failed to download ${name}`);
    } finally {
      setColDownloading((prev) => ({ ...prev, [name]: false }));
    }
  };

  // ─── S3 backup helpers ─────────────────────────────────────────────────────
  const fetchS3Backups = async () => {
    setS3Loading(true);
    try {
      const res = await api.get("/superAdmin/backup/s3");
      setS3Backups(res.data.backups || []);
    } catch (_) {}
    finally { setS3Loading(false); }
  };

  const handleS3Backup = async () => {
    setS3Uploading(true);
    antMessage.loading({ content: "Uploading backup to S3...", key: "s3backup", duration: 0 });
    try {
      const res = await api.post("/superAdmin/backup/s3");
      antMessage.success({ content: `Backup uploaded to S3 (${(res.data.sizeBytes / 1024 / 1024).toFixed(1)} MB, ${res.data.collections} collections)`, key: "s3backup", duration: 4 });
      fetchS3Backups();
    } catch (err) {
      antMessage.error({ content: err.response?.data?.message || "S3 backup failed", key: "s3backup", duration: 4 });
    } finally { setS3Uploading(false); }
  };

  // ─── Device fetch ──────────────────────────────────────────────────────────
  useEffect(() => {
    dispatch(fetchDevices());
  }, [dispatch]);

  // ─── Main data fetch ───────────────────────────────────────────────────────
  useEffect(() => {
    fetchCompanyAdmins(pagination.current, pagination.pageSize, searchQuery, filtersRef.current);
    // eslint-disable-next-line
  }, [pagination.current, pagination.pageSize, searchQuery]);

  const fetchCompanyAdmins = async (
    page = 1,
    limit = 20,
    search = "",
    f = DEFAULT_FILTERS,
    forceRefresh = false
  ) => {
    if (cachedPages[page] && search === searchQuery && !forceRefresh) {
      dispatch(getCompaniesPage(page));
      return;
    }
    dispatch(setLoading(true));
    try {
      // Build params — send all active filters to backend
      const params = { page, limit, search };
      // "all" = include everything (no filter); otherwise send value to backend
      if (f.accountStatus !== "all") params.accountStatus = f.accountStatus;
      if (f.extStatus !== "all") params.extensionStatus = f.extStatus === "active";
      if (f.device !== "all") params.deviceId = f.device;
      if (f.plan !== "all") params.planStatus = f.plan;
      if (f.paused !== "all") params.accessPaused = f.paused === "paused";
      if (f.dateRange?.[0]) params.startDate = f.dateRange[0].format("YYYY-MM-DD");
      if (f.dateRange?.[1]) params.endDate = f.dateRange[1].format("YYYY-MM-DD");

      const response = await api.post("/superAdmin/allCompanyAdmin", params);
      if (response.data.status === "success") {
        dispatch(
          setCompaniesPage({
            page,
            data: response.data.data,
            pagination: {
              current: response.data.page,
              pageSize: response.data.limit,
              total: response.data.totalAdmins,
            },
          })
        );
      }
    } catch (error) {
      dispatch(setError(error.message));
      dispatch(showToast({ heading: "Error", message: "Failed to fetch company admins", variant: "danger" }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  const handleBulkDeactivate = async () => {
    setBulkDeleting(true);
    try {
      await api.delete("/deleteUser/bulk", { data: { companyIds: selectedRowKeys } });
      dispatch(showToast({ message: `${selectedRowKeys.length} companies deactivated`, variant: "success" }));
      setSelectedRowKeys([]);
      dispatch(clearCompaniesCache());
      fetchCompanyAdmins(1, pagination.pageSize, searchQuery, filtersRef.current, true);
    } catch (err) {
      dispatch(showToast({ message: err?.response?.data?.message || "Bulk deactivate failed", variant: "danger" }));
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleTableChange = (pag) => {
    if (pag.pageSize !== pagination.pageSize) {
      // Page size changed — setPageSize clears cache and resets to page 1
      dispatch(setPageSize(pag.pageSize));
    } else {
      dispatch(setCurrentPage(pag.current));
    }
  };

  // Debounced search → update Redux + clear cache
  useEffect(() => {
    if (debouncedSearch !== searchQuery) {
      dispatch(setSearchQuery(debouncedSearch));
      dispatch(clearCompaniesCache());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // Reset on mount / unmount — force a fresh fetch so the default "not_deactivated"
  // filter is always applied even if Redux has stale cached data from a prior visit
  useEffect(() => {
    setLocalSearch("");
    dispatch(setSearchQuery(""));
    dispatch(setCurrentPage(1));
    dispatch(clearCompaniesCache());
    fetchCompanyAdmins(1, 20, "", DEFAULT_FILTERS, true);
    return () => {
      setLocalSearch("");
      dispatch(setSearchQuery(""));
      dispatch(setCurrentPage(1));
      dispatch(clearCompaniesCache());
    };
  }, []); // eslint-disable-line

  // ─── Action handlers ───────────────────────────────────────────────────────
  const handleViewDetails = (admin) => {
    navigate(all_routes.companyDetails, { state: { company: admin } });
  };

  const handleToggleAccountStatus = async (userId, currentStatus) => {
    const toggleKey = `account-${userId}`;
    setLoadingToggles((prev) => ({ ...prev, [toggleKey]: true }));
    try {
      const newStatus = currentStatus === "active" ? "suspended" : "active";
      const endpoint = newStatus === "active" ? "/deleteUser/activate" : "/deleteUser/suspend";
      const response = await api.post(endpoint, { user_id: userId });
      if (response.data.status === "success") {
        await new Promise((resolve) => setTimeout(resolve, 800));
        dispatch(showToast({ heading: "Success", message: response.data.message, variant: "success" }));
        await fetchCompanyAdmins(pagination.current, pagination.pageSize, searchQuery, filtersRef.current, true);
      }
    } catch (error) {
      dispatch(showToast({ heading: "Error", message: error?.response?.data?.message || "Failed to update account status", variant: "danger" }));
    } finally {
      setLoadingToggles((prev) => { const s = { ...prev }; delete s[toggleKey]; return s; });
    }
  };

  const handleToggleExtensionStatus = async (userId, company, checked) => {
    const toggleKey = `extension-${userId}`;
    setLoadingToggles((prev) => ({ ...prev, [toggleKey]: true }));
    try {
      const response = await api.put("/superAdmin/editCompanyAdminAndAgent", {
        userId,
        extensionNumber: company.extensionNumber,
        telephone: company.telephone,
        sipSecret: company.sipSecret,
        status: checked,
      });
      if (response.data.status === "success") {
        await new Promise((resolve) => setTimeout(resolve, 800));
        dispatch(showToast({ heading: "Success", message: checked ? "Extension activated successfully" : "Extension deactivated successfully", variant: "success" }));
        await fetchCompanyAdmins(pagination.current, pagination.pageSize, searchQuery, filtersRef.current, true);
      }
    } catch (error) {
      dispatch(showToast({ heading: "Error", message: error?.response?.data?.error || "Failed to update extension status", variant: "danger" }));
    } finally {
      setLoadingToggles((prev) => { const s = { ...prev }; delete s[toggleKey]; return s; });
    }
  };

  const handlePauseAccess = async (userId) => {
    setPlanActionLoading((prev) => ({ ...prev, [`pause-${userId}`]: true }));
    try {
      await dispatch(pauseUserAccess(userId)).unwrap();
      await fetchCompanyAdmins(pagination.current, pagination.pageSize, searchQuery, filtersRef.current, true);
    } catch (_) {}
    finally { setPlanActionLoading((prev) => { const s = { ...prev }; delete s[`pause-${userId}`]; return s; }); }
  };

  const handleResumeAccess = async (userId) => {
    setPlanActionLoading((prev) => ({ ...prev, [`resume-${userId}`]: true }));
    try {
      await dispatch(resumeUserAccess(userId)).unwrap();
      await fetchCompanyAdmins(pagination.current, pagination.pageSize, searchQuery, filtersRef.current, true);
    } catch (_) {}
    finally { setPlanActionLoading((prev) => { const s = { ...prev }; delete s[`resume-${userId}`]; return s; }); }
  };

  const handleCancelSubscription = async (userId) => {
    setPlanActionLoading((prev) => ({ ...prev, [userId]: true }));
    try {
      await dispatch(cancelUserSubscription(userId)).unwrap();
      await fetchCompanyAdmins(pagination.current, pagination.pageSize, searchQuery, filtersRef.current, true);
    } catch (_) {}
    finally { setPlanActionLoading((prev) => { const s = { ...prev }; delete s[userId]; return s; }); }
  };

  const handleDeleteCompany = (record) => {
    Modal.confirm({
      title: "Permanently Delete Company",
      content: (
        <div>
          <p>Are you sure you want to <strong>permanently delete</strong> this company and all its agents?</p>
          <p style={{ color: "#ef4444", fontWeight: 600 }}>
            {record.userInfo?.companyName || `${record.firstname} ${record.lastname}`}
          </p>
          <p style={{ color: "#6b7280", fontSize: 13 }}>
            This will delete the company admin and all associated agents. This action <strong>cannot be undone</strong>.
          </p>
        </div>
      ),
      okText: "Delete Permanently",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const response = await api.post("/superAdmin/deleteCompany", { userId: record._id });
          if (response.data.status === "success") {
            dispatch(showToast({ heading: "Deleted", message: "Company permanently deleted", variant: "success" }));
            await fetchCompanyAdmins(pagination.current, pagination.pageSize, searchQuery, filtersRef.current, true);
          }
        } catch (error) {
          dispatch(showToast({ heading: "Error", message: error?.response?.data?.error || "Failed to delete company", variant: "danger" }));
        }
      },
    });
  };

  // ── Create Company Admin ──────────────────────────────────────────────────
  const handleCreateAdmin = async () => {
    try {
      const values = await createAdminForm.validateFields();
      setCreateAdminLoading(true);
      const emailVerified = !!values.emailVerified;
      const mobileVerified = !!values.mobileVerified;
      await api.post("/superAdmin/createCompanyAdmin", {
        firstname: values.firstname,
        lastname: values.lastname,
        email: values.email,
        password: values.password,
        companyName: values.companyName,
        mobile: values.mobile ? { countryCode: values.countryCode || "", number: values.mobile } : undefined,
        emailVerified,
        mobileVerified,
        isVerified: emailVerified && mobileVerified,
      });
      dispatch(showToast({ message: "Company admin created successfully", variant: "success" }));
      createAdminForm.resetFields();
      setCreateAdminModal(false);
      dispatch(clearCompaniesCache());
      fetchCompanyAdmins(1, pagination.pageSize, searchQuery, filtersRef.current, true);
    } catch (err) {
      if (err?.errorFields) return;
      dispatch(showToast({ message: err?.response?.data?.error || "Failed to create company admin", variant: "error" }));
    } finally {
      setCreateAdminLoading(false);
    }
  };

  const handleCopyLoginLink = async (userId) => {
    try {
      const res = await api.post("/superAdmin/generateLoginLink", { userId });
      await navigator.clipboard.writeText(res.data.link);
      dispatch(showToast({ message: "Login link copied to clipboard", variant: "success" }));
    } catch (err) {
      dispatch(showToast({ message: err?.response?.data?.error || "Failed to generate login link", variant: "danger" }));
    }
  };

  const openTrialModal = (record) => {
    setTrialTarget(record);
    const days = record.trialDurationDays || 7;
    trialForm.setFieldsValue({ trialDays: days });
    setTrialPreviewDays(days);
    setTrialModal(true);
  };

  const handleSaveTrial = async () => {
    const { trialDays } = await trialForm.validateFields();
    setTrialSaving(true);
    try {
      await dispatch(updateUserTrialPeriod({ userId: trialTarget._id, durationDays: trialDays })).unwrap();
      setTrialModal(false);
      await fetchCompanyAdmins(pagination.current, pagination.pageSize, searchQuery, filtersRef.current, true);
    } catch (_) {}
    finally { setTrialSaving(false); }
  };

  const openBalanceModal = (record) => {
    setBalanceTarget(record);
    balanceForm.setFieldsValue({ mode: "set", amount: Number(record.creditBalance || 0).toFixed(2), note: "" });
    setBalanceModal(true);
  };

  const handleSaveBalance = async () => {
    const values = await balanceForm.validateFields();
    setBalanceSaving(true);
    try {
      const res = await api.put("/superAdmin/adjustBalance", {
        userId: balanceTarget._id,
        amount: Number(values.amount),
        mode:   values.mode,
        note:   values.note || "",
      });
      antMessage.success(
        `Balance updated — new balance: $${Number(res.data.newBalance).toFixed(2)}`
      );
      setBalanceModal(false);
      // Refresh list so the balance column reflects the new value
      await fetchCompanyAdmins(pagination.current, pagination.pageSize, searchQuery, filtersRef.current, true);
    } catch (err) {
      antMessage.error(err?.response?.data?.error || "Failed to update balance");
    } finally {
      setBalanceSaving(false);
    }
  };

  // ── Extension management ──────────────────────────────────────────────────
  const openExtModal = async (record) => {
    setExtTarget(record);
    setExtList([]);
    setExtModal(true);
    setExtLoading(true);
    try {
      const res = await api.get(`/superAdmin/companies/${record._id}/extensions`);
      setExtList(res.data.extensions || []);
    } catch {
      antMessage.error("Failed to load extensions");
    } finally {
      setExtLoading(false);
    }
  };

  const handleAddExtension = async () => {
    const values = await extForm.validateFields();
    const duplicate = extList.some(
      (e) => e.extensionNumber === values.extensionNumber.trim()
    );
    if (duplicate) {
      antMessage.error("Extension number already exists");
      return;
    }
    setExtList((prev) => [
      ...prev,
      { extensionNumber: values.extensionNumber.trim(), PBX_TELEPHONE: values.PBX_TELEPHONE.trim() },
    ]);
    extForm.resetFields();
  };

  const handleRemoveExtension = (extensionNumber) => {
    setExtList((prev) => prev.filter((e) => e.extensionNumber !== extensionNumber));
  };

  const handleSaveExtensions = async () => {
    setExtSaving(true);
    try {
      await api.put(`/superAdmin/companies/${extTarget._id}/extensions`, { extensions: extList });
      antMessage.success("Extensions saved");
      setExtModal(false);
    } catch {
      antMessage.error("Failed to save extensions");
    } finally {
      setExtSaving(false);
    }
  };

  // ─── Derived data ──────────────────────────────────────────────────────────
  const deviceMap = React.useMemo(() => {
    const map = {};
    devices?.forEach((d) => { map[d.deviceId] = d.deviceName; });
    return map;
  }, [devices]);

  const planColorMap = {
    trial: "orange", active: "green", paused: "gold",
    expired: "red", cancelled: "red", none: "default",
  };

  // ─── Column definitions ────────────────────────────────────────────────────
  const adminColumns = [
    {
      title: "Name",
      key: "name",
      render: (_, record) => {
        const deviceName = deviceMap[record?.PBXDetails?.assignedDeviceId] || "N/A";
        const phone = record.phonenumbers?.[0]
          ? `${record.phonenumbers[0].countryCode || ""} ${record.phonenumbers[0].number || ""}`.trim()
          : "No phone";

        const popoverContent = (
          <div style={{ width: 290 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, paddingBottom: 12, borderBottom: "1px solid #f0f0f0" }}>
              <span style={{ width: 40, height: 40, borderRadius: "50%", background: "#6366f1", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 16, flexShrink: 0 }}>
                {record.firstname?.[0]}{record.lastname?.[0]}
              </span>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, lineHeight: 1.3 }}>{record.firstname} {record.lastname}</div>
                <div style={{ color: "#6b7280", fontSize: 12 }}>{record.userInfo?.companyName || "—"}</div>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 7, fontSize: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <span><MailOutlined style={{ marginRight: 5, color: "#9ca3af" }} />{record.email}</span>
                <Tag
                  color={record.emailVerified ? "green" : "orange"}
                  style={{ margin: 0, fontSize: 10, lineHeight: "16px", padding: "0 5px" }}
                >
                  {record.emailVerified ? "✓ Verified" : "Pending"}
                </Tag>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <span><PhoneOutlined style={{ marginRight: 5, color: "#9ca3af" }} />{phone}</span>
                <Tag
                  color={record.isVerified ? "green" : "orange"}
                  style={{ margin: 0, fontSize: 10, lineHeight: "16px", padding: "0 5px" }}
                >
                  {record.isVerified ? "✓ Verified" : "Pending"}
                </Tag>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ color: "#9ca3af", minWidth: 72 }}>Extension:</span>
                <Tag color="blue" style={{ margin: 0 }}>{record?.PBXDetails?.PBX_EXTENSION_NUMBER || "N/A"}</Tag>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ color: "#9ca3af", minWidth: 72 }}>Device:</span>
                <span>{deviceName}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ color: "#9ca3af", minWidth: 72 }}>Plan:</span>
                <Tag color={planColorMap[record.planStatus || "none"] || "default"} style={{ margin: 0, textTransform: "capitalize" }}>
                  {record.planStatus || "None"}
                </Tag>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ color: "#9ca3af", minWidth: 72 }}>Created:</span>
                <span>{new Date(record.createdAt).toLocaleDateString()}</span>
              </div>
              {record.accessPausedByAdmin && (
                <Tag color="orange" style={{ width: "fit-content", margin: 0 }}>⏸ Access Paused</Tag>
              )}
            </div>
          </div>
        );
        return (
          <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, minWidth: 140 }}>
            <span
              style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", color: "#1677ff", fontWeight: 500 }}
              onClick={() => handleViewDetails(record)}
            >
              {record.isVerified ? (
                <img
                  src="/assets/img/icons/verifiedTick.webp"
                  alt="Verified"
                  style={{ width: 16, height: 16, verticalAlign: "middle", flexShrink: 0 }}
                />
              ) : (
                <UserOutlined style={{ color: "#9ca3af", flexShrink: 0 }} />
              )}
              {record.firstname} {record.lastname}
            </span>
            <Popover
              content={popoverContent}
              title={null}
              trigger={["hover", "click"]}
              placement="rightTop"
              overlayStyle={{ maxWidth: 320 }}
            >
              <InfoCircleOutlined style={{ color: "#6366f1", cursor: "pointer", fontSize: 13, flexShrink: 0, opacity: 0.65 }} />
            </Popover>
          </span>
        );
      },
    },

    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email, record) => (
        <span>
          {record.emailVerified ? (
            <img
              src="/assets/img/icons/verifiedTick.webp"
              alt="Verified"
              style={{ width: 16, height: 16, marginRight: 8, verticalAlign: "middle" }}
            />
          ) : (
            <MailOutlined style={{ marginRight: 8 }} />
          )}
          {email}
        </span>
      ),
    },
    {
      title: "Company Name",
      dataIndex: "userInfo",
      key: "companyName",
      render: (userInfo) => <span>{userInfo?.companyName || "N/A"}</span>,
    },
    {
      title: "Registered",
      key: "registeredAt",
      render: (_, record) =>
        record.createdAt ? (
          <span style={{ fontSize: 12, color: "#374151", whiteSpace: "nowrap" }}>
            <div>{dayjs(record.createdAt).format("DD MMM YYYY")}</div>
            <div style={{ color: "#9ca3af", fontSize: 11 }}>{dayjs(record.createdAt).format("hh:mm A")}</div>
          </span>
        ) : (
          <span style={{ color: "#9ca3af" }}>—</span>
        ),
    },
    {
      title: "Ext Status",
      dataIndex: "extensionStatus",
      key: "extensionStatus",
      render: (extensionStatus, record) => {
        const toggleKey = `extension-${record._id}`;
        return (
          <Switch
            checked={extensionStatus}
            loading={loadingToggles[toggleKey]}
            onChange={(checked) => handleToggleExtensionStatus(record._id, record, checked)}
            checkedChildren="Active"
            unCheckedChildren="Inactive"
            size="large"
          />
        );
      },
    },
    {
      title: "Device",
      key: "deviceName",
      render: (_, record) => {
        const matched = devices?.find((d) => d.deviceId === record?.PBXDetails?.assignedDeviceId);
        return <span>{matched?.deviceName || "N/A"}</span>;
      },
    },
    {
      title: "Account",
      dataIndex: "accountStatus",
      key: "accountStatus",
      render: (accountStatus, record) => {
        const toggleKey = `account-${record._id}`;
        return (
          <Switch
            checked={accountStatus === "active"}
            loading={loadingToggles[toggleKey]}
            onChange={() => handleToggleAccountStatus(record._id, accountStatus)}
            checkedChildren="Active"
            unCheckedChildren="Suspended"
            size="large"
          />
        );
      },
    },
    {
      title: "Plan",
      key: "plan",
      render: (_, record) => {
        const status = record.planStatus;
        const planName = record.subscription?.planId?.name;
        const billingPeriod = record.subscription?.billingPeriod;
        const periodColorMap = { monthly: "blue", quarterly: "purple", yearly: "cyan" };
        const label = planName || (status === "trial" ? "Free Trial" : status || "None");
        return (
          <Space size={4}>
            <Tag color={planColorMap[status] || "default"}>{label}</Tag>
            {billingPeriod && (
              <Tag color={periodColorMap[billingPeriod] || "default"} style={{ textTransform: "capitalize" }}>
                {billingPeriod}
              </Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 60,
      render: (_, record) => {
        const pauseLoading = !!planActionLoading[`pause-${record._id}`];
        const resumeLoading = !!planActionLoading[`resume-${record._id}`];

        const isSuspended = record.accountStatus === "suspended";

        const menuItems = [
          {
            key: "view",
            label: "View Details",
            icon: <EyeOutlined />,
            onClick: () => handleViewDetails(record),
          },
          {
            key: "trial",
            label: "Adjust Trial Period",
            icon: <ClockCircleOutlined />,
            onClick: () => openTrialModal(record),
          },
          {
            key: "copy-login",
            label: "Copy Login Link",
            icon: <CopyOutlined />,
            onClick: () => handleCopyLoginLink(record._id),
          },
          {
            key: "balance",
            label: "Adjust Balance",
            icon: <DollarOutlined style={{ color: "#10b981" }} />,
            onClick: () => openBalanceModal(record),
          },
          {
            key: "extensions",
            label: "Manage Extensions",
            icon: <ApartmentOutlined style={{ color: "#6366f1" }} />,
            onClick: () => openExtModal(record),
          },
          record.accessPausedByAdmin
            ? {
                key: "resume",
                label: resumeLoading ? "Resuming…" : "Resume Access",
                icon: <PlayCircleOutlined style={{ color: "#10b981" }} />,
                onClick: () => handleResumeAccess(record._id),
                disabled: resumeLoading,
              }
            : {
                key: "pause",
                label: pauseLoading ? "Pausing…" : "Pause Access",
                icon: <PauseCircleOutlined style={{ color: "#f59e0b" }} />,
                onClick: () => handlePauseAccess(record._id),
                disabled: pauseLoading,
              },
          ...(isSuspended ? [
            { type: "divider", key: "divider-del" },
            {
              key: "delete",
              label: "Delete Company",
              icon: <DeleteOutlined />,
              danger: true,
              onClick: () => handleDeleteCompany(record),
            },
          ] : []),
        ];

        return (
          <Dropdown menu={{ items: menuItems }} trigger={["click"]} placement="bottomRight">
            <Button
              type="text"
              icon={<MoreOutlined style={{ fontSize: 18 }} />}
              style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
            />
          </Dropdown>
        );
      },
    },
  ];

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="page-header">
                <div className="row">
                  <div className="col-sm-12">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h3 className="page-title mt-2 mt-md-0">Manage Companies</h3>
                        <ul className="breadcrumb">
                          <li className="breadcrumb-item">
                            <Link to={all_routes.adminDashboard}>Dashboard</Link>
                          </li>
                          <li className="breadcrumb-item active">Companies</li>
                        </ul>
                      </div>
                      <Space>
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => { createAdminForm.resetFields(); setCreateAdminModal(true); }}>
                          Create Company Admin
                        </Button>
                        <Button icon={<DatabaseOutlined />} onClick={openBackupModal}>
                          Database Backup
                        </Button>
                      </Space>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-body table-responsive custom-table">
                  {/* Header row */}
                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-3">
                    <h5 className="fw-semibold mb-2 mt-2">All Company Admins</h5>
                    <div className="d-flex gap-2 align-items-center">
                      {selectedRowKeys.length > 0 && (
                        <Popconfirm
                          title={`Deactivate ${selectedRowKeys.length} selected companies?`}
                          onConfirm={handleBulkDeactivate}
                          okText="Deactivate"
                          okButtonProps={{ danger: true }}
                        >
                          <Button danger loading={bulkDeleting}>
                            Deactivate Selected ({selectedRowKeys.length})
                          </Button>
                        </Popconfirm>
                      )}
                      <Input
                        placeholder="Search by name, email, phone..."
                        value={localSearch}
                        onChange={(e) => setLocalSearch(e.target.value)}
                        style={{ width: 280 }}
                        allowClear
                      />
                    </div>
                  </div>

                  {/* Always-visible server-side filter bar */}
                  <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px 16px", marginBottom: 16 }}>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
                      <Select
                        style={{ width: 150 }}
                        value={filters.accountStatus}
                        onChange={(v) => handleFilterChange("accountStatus", v)}
                        options={[
                          { value: "active", label: "Active Companies" },
                          { value: "suspended", label: "Suspended Companies" },
                          { value: "all", label: "All Companies" },
                        ]}
                      />
                      <Select
                        style={{ width: 140 }}
                        value={filters.extStatus}
                        onChange={(v) => handleFilterChange("extStatus", v)}
                        options={[
                          { value: "all", label: "All Ext Status" },
                          { value: "active", label: "Ext Active" },
                          { value: "inactive", label: "Ext Inactive" },
                        ]}
                      />
                      <Select
                        style={{ width: 150 }}
                        value={filters.device}
                        onChange={(v) => handleFilterChange("device", v)}
                        options={[
                          { value: "all", label: "All Devices" },
                          ...(devices || []).map((d) => ({ value: d.deviceId, label: d.deviceName })),
                        ]}
                      />
                      <Select
                        style={{ width: 140 }}
                        value={filters.plan}
                        onChange={(v) => handleFilterChange("plan", v)}
                        options={[
                          { value: "all", label: "All Plans" },
                          { value: "trial", label: "Trial" },
                          { value: "active", label: "Active Plan" },
                          { value: "paused", label: "Paused Plan" },
                          { value: "expired", label: "Expired" },
                          { value: "cancelled", label: "Cancelled" },
                          { value: "none", label: "No Plan" },
                        ]}
                      />
                      <Select
                        style={{ width: 150 }}
                        value={filters.paused}
                        onChange={(v) => handleFilterChange("paused", v)}
                        options={[
                          { value: "all", label: "All Access" },
                          { value: "paused", label: "Access Paused" },
                          { value: "active", label: "Access Active" },
                        ]}
                      />
                      <DatePicker.RangePicker
                        style={{ width: 244 }}
                        value={filters.dateRange}
                        onChange={(v) => handleFilterChange("dateRange", v)}
                        placeholder={["Created From", "Created To"]}
                        format="DD MMM YYYY"
                      />
                      <Button onClick={clearFilters}>Clear</Button>
                    </div>
                  </div>

                  <Table
                    columns={adminColumns}
                    dataSource={companies}
                    rowKey="_id"
                    loading={loading}
                    rowSelection={rowSelection}
                    pagination={{
                      current: pagination.current,
                      pageSize: pagination.pageSize,
                      total: pagination.total,
                      showSizeChanger: true,
                    }}
                    onChange={handleTableChange}
                    bordered
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Company Admin Modal */}
      <Modal
        title={<span><UserAddOutlined style={{ marginRight: 8, color: "#6366f1" }} />Create Company Admin</span>}
        open={createAdminModal}
        onCancel={() => setCreateAdminModal(false)}
        onOk={handleCreateAdmin}
        okText="Create"
        confirmLoading={createAdminLoading}
        width={480}
      >
        <Form form={createAdminForm} layout="vertical" style={{ marginTop: 12 }}>
          <div style={{ display: "flex", gap: 12 }}>
            <Form.Item name="firstname" label="First Name" rules={[{ required: true, message: "Required" }]} style={{ flex: 1 }}>
              <Input placeholder="John" />
            </Form.Item>
            <Form.Item name="lastname" label="Last Name" style={{ flex: 1 }}>
              <Input placeholder="Doe" />
            </Form.Item>
          </div>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: "email", message: "Valid email required" }]}>
            <Input placeholder="admin@company.com" />
          </Form.Item>
          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: "Password is required" },
              {
                validator(_, value) {
                  if (!value) return Promise.resolve();
                  if (value.length < 8) return Promise.reject(new Error("At least 8 characters required"));
                  if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])/.test(value))
                    return Promise.reject(new Error("Must include uppercase, lowercase, number and special character (@$!%*?&#)"));
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input.Password placeholder="Password" />
          </Form.Item>
          <Form.Item name="companyName" label="Company Name">
            <Input placeholder="California" />
          </Form.Item>
          <div style={{ display: "flex", gap: 12 }}>
            <Form.Item name="countryCode" label="Country Code" style={{ width: 110 }}>
              <Input placeholder="+1" />
            </Form.Item>
            <Form.Item name="mobile" label="Mobile (optional)" style={{ flex: 1 }}>
              <Input placeholder="5551234567" />
            </Form.Item>
          </div>
          <Form.Item name="emailVerified" valuePropName="checked">
            <Checkbox>Mark email as verified</Checkbox>
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.emailVerified !== cur.emailVerified || prev.firstname !== cur.firstname || prev.lastname !== cur.lastname}>
            {({ getFieldValue }) => {
              const emailOk = !!getFieldValue("emailVerified");
              const hasName = !!(getFieldValue("firstname") || "").trim() && !!(getFieldValue("lastname") || "").trim();
              return emailOk && hasName ? (
                <Form.Item name="mobileVerified" valuePropName="checked">
                  <Checkbox>Mark mobile as verified</Checkbox>
                </Form.Item>
              ) : null;
            }}
          </Form.Item>
        </Form>
      </Modal>

      {/* Trial Period Modal */}
      <Modal
        title={
          <span>
            <ClockCircleOutlined style={{ marginRight: 8, color: "#6366f1" }} />
            Set Trial Period
          </span>
        }
        open={trialModal}
        onCancel={() => setTrialModal(false)}
        onOk={handleSaveTrial}
        okText="Save"
        confirmLoading={trialSaving}
        width={380}
      >
        {trialTarget && (
          <>
            <div style={{ marginBottom: 12, fontSize: 13, color: "#374151" }}>
              <strong>
                {trialTarget.userInfo?.companyName ||
                  `${trialTarget.firstname || ""} ${trialTarget.lastname || ""}`.trim() || "—"}
              </strong>
            </div>
            <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
              <div style={{ flex: 1, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", fontSize: 12 }}>
                <div style={{ color: "#6b7280", marginBottom: 2 }}>Current end date</div>
                <strong style={{ color: "#374151" }}>
                  {trialTarget.trialStartedAt
                    ? new Date(new Date(trialTarget.trialStartedAt).getTime() + (trialTarget.trialDurationDays || 7) * 86400000).toLocaleDateString()
                    : "—"}
                </strong>
              </div>
              <div style={{ flex: 1, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: "10px 12px", fontSize: 12 }}>
                <div style={{ color: "#3b82f6", marginBottom: 2 }}>New end date</div>
                <strong style={{ color: "#1d4ed8" }}>
                  {trialTarget.trialStartedAt && trialPreviewDays >= 1
                    ? new Date(new Date(trialTarget.trialStartedAt).getTime() + trialPreviewDays * 86400000).toLocaleDateString()
                    : "—"}
                </strong>
              </div>
            </div>
          </>
        )}
        <Form form={trialForm} layout="vertical">
          <Form.Item
            label="Trial duration (days)"
            name="trialDays"
            rules={[{ required: true, type: "number", min: 1, message: "Enter at least 1 day" }]}
          >
            <InputNumber
              min={1}
              style={{ width: "100%" }}
              onChange={(val) => setTrialPreviewDays(val || 1)}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Adjust Balance Modal */}
      <Modal
        title={
          <span>
            <DollarOutlined style={{ color: "#10b981", marginRight: 8 }} />
            Adjust Balance —{" "}
            <span style={{ fontWeight: 400, fontSize: 13, color: "#6b7280" }}>
              {balanceTarget?.userInfo?.companyName ||
                `${balanceTarget?.firstname || ""} ${balanceTarget?.lastname || ""}`.trim() || ""}
            </span>
          </span>
        }
        open={balanceModal}
        onCancel={() => setBalanceModal(false)}
        onOk={handleSaveBalance}
        okText="Save"
        confirmLoading={balanceSaving}
        width={400}
      >
        {balanceTarget && (
          <>
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8,
              padding: "10px 14px", marginBottom: 16, fontSize: 13,
            }}>
              <span style={{ color: "#6b7280" }}>Current balance</span>
              <span style={{
                fontWeight: 700, fontSize: 16,
                color: (balanceTarget.creditBalance || 0) < 5 ? "#ef4444" : "#10b981",
              }}>
                ${Number(balanceTarget.creditBalance || 0).toFixed(2)}
              </span>
            </div>
            <Form form={balanceForm} layout="vertical">
              <Form.Item
                name="mode"
                label="Operation"
                rules={[{ required: true }]}
              >
                <Select>
                  <Select.Option value="set">Set to exact amount</Select.Option>
                  <Select.Option value="add">Add to balance (top-up)</Select.Option>
                  <Select.Option value="subtract">Subtract from balance (deduct)</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item
                name="amount"
                label="Amount ($)"
                rules={[
                  { required: true, message: "Enter an amount" },
                  { validator: (_, v) => Number(v) >= 0 ? Promise.resolve() : Promise.reject("Amount must be ≥ 0") },
                ]}
              >
                <InputNumber
                  min={0}
                  precision={2}
                  step={1}
                  prefix="$"
                  style={{ width: "100%" }}
                />
              </Form.Item>
              <Form.Item name="note" label="Note (optional)">
                <Input placeholder="e.g. Manual top-up by admin" maxLength={200} />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      {/* Manage Extensions Modal */}
      <Modal
        title={
          <Space>
            <ApartmentOutlined style={{ color: "#6366f1" }} />
            <span>
              Manage Extensions
              {extTarget ? ` — ${extTarget.firstname || ""} ${extTarget.lastname || ""}`.trim() : ""}
            </span>
          </Space>
        }
        open={extModal}
        onCancel={() => setExtModal(false)}
        onOk={handleSaveExtensions}
        okText="Save"
        confirmLoading={extSaving}
        width={520}
      >
        {extLoading ? (
          <div style={{ textAlign: "center", padding: 24 }}>Loading…</div>
        ) : (
          <>
            {/* Current extensions list */}
            {extList.length > 0 ? (
              <div style={{ marginBottom: 16 }}>
                {extList.map((e) => (
                  <div
                    key={e.extensionNumber}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "8px 12px", marginBottom: 6,
                      background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6,
                    }}
                  >
                    <span style={{ fontSize: 13 }}>
                      <strong>Ext {e.extensionNumber}</strong>
                      <span style={{ color: "#6b7280", marginLeft: 8 }}>{e.PBX_TELEPHONE}</span>
                    </span>
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveExtension(e.extensionNumber)}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: "#9ca3af", fontSize: 13, marginBottom: 16 }}>
                No extensions assigned yet.
              </div>
            )}

            {/* Add new extension form */}
            <Form form={extForm} layout="inline" style={{ marginTop: 8 }}>
              <Form.Item
                name="extensionNumber"
                rules={[{ required: true, message: "Required" }]}
                style={{ flex: 1 }}
              >
                <Input placeholder="Extension (e.g. 1010)" />
              </Form.Item>
              <Form.Item
                name="PBX_TELEPHONE"
                rules={[{ required: true, message: "Required" }]}
                style={{ flex: 1.5 }}
              >
                <Input placeholder="Phone number (e.g. 971585990233)" />
              </Form.Item>
              <Form.Item>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAddExtension}>
                  Add
                </Button>
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      {/* Database Backup Modal */}
      <Modal
        title={<Space><DatabaseOutlined /><span>Database Backup</span></Space>}
        open={backupModal}
        onCancel={() => setBackupModal(false)}
        footer={null}
        width={560}
      >
        <div style={{ marginBottom: 16, padding: "12px 16px", background: "#f9fafb", borderRadius: 8, border: "1px solid #e5e7eb" }}>
          <p style={{ margin: 0, fontSize: 13, color: "#374151" }}>
            <strong>For MongoDB Compass:</strong> Download a single collection JSON file below, then in Compass go to that collection → <em>ADD DATA → Import JSON file</em>.
          </p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
          <div>
            <Button icon={<DatabaseOutlined />} loading={mongodumpLoading} onClick={handleDownloadMongodump} type="primary" block>
              {mongodumpLoading ? "Running mongodump..." : "Download mongodump Backup (.gz)"}
            </Button>
            <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 4, marginBottom: 0 }}>
              Native BSON format. Restore with: <code>mongorestore --uri "..." --gzip --archive=voycell-mongodump.gz</code>
            </p>
          </div>
          <div>
            <Button icon={<DatabaseOutlined />} loading={backupLoading} onClick={handleDownloadAll} block>
              {backupLoading ? "Preparing..." : "Download JSON Backup (ZIP + restore script)"}
            </Button>
            <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 4, marginBottom: 0 }}>
              Extended JSON format. Works with Compass, Atlas, and mongoimport.
            </p>
          </div>
        </div>
        <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 16 }}>
          <p style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>Individual Collections (Compass-ready JSON)</p>
          {collectionsLoading ? (
            <div style={{ textAlign: "center", padding: "20px 0", color: "#9ca3af" }}>Loading collections...</div>
          ) : (
            <div style={{ maxHeight: 320, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
              {collections.map((name) => (
                <div key={name} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#f9fafb", borderRadius: 6, border: "1px solid #e5e7eb" }}>
                  <span style={{ fontSize: 13, fontFamily: "monospace" }}>{name}.json</span>
                  <Button size="small" loading={!!colDownloading[name]} onClick={() => handleDownloadCollection(name)}>
                    Download
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── S3 Cloud Backups ── */}
        <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div>
              <p style={{ fontWeight: 700, fontSize: 13, margin: 0 }}>☁️ S3 Cloud Backups</p>
              <p style={{ fontSize: 12, color: "#9ca3af", margin: 0 }}>Auto-backup every 2 hours · 7-day retention · stored in AWS S3</p>
            </div>
            <Space>
              <Button size="small" onClick={fetchS3Backups} loading={s3Loading}>Refresh</Button>
              <Button size="small" type="primary" loading={s3Uploading} onClick={handleS3Backup}
                style={{ background: "#3C2371", borderColor: "#3C2371" }}>
                Backup to S3 Now
              </Button>
            </Space>
          </div>
          {s3Loading ? (
            <div style={{ textAlign: "center", padding: "16px 0", color: "#9ca3af" }}>Loading...</div>
          ) : s3Backups.length === 0 ? (
            <div style={{ textAlign: "center", padding: "16px 0", color: "#9ca3af", fontSize: 13 }}>
              No cloud backups yet. Click "Backup to S3 Now" to create the first one.
            </div>
          ) : (
            <div style={{ maxHeight: 220, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
              {s3Backups.map((b) => (
                <div key={b.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#f0fdf4", borderRadius: 6, border: "1px solid #bbf7d0" }}>
                  <div>
                    <div style={{ fontSize: 12, fontFamily: "monospace", fontWeight: 600, color: "#374151" }}>{b.filename}</div>
                    <div style={{ fontSize: 11, color: "#9ca3af" }}>
                      {new Date(b.lastModified).toLocaleString()} · {(b.sizeBytes / 1024 / 1024).toFixed(1)} MB
                    </div>
                  </div>
                  <Button size="small" href={b.downloadUrl} target="_blank" type="link" icon={<DownloadOutlined />}>
                    Download
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default ManageCompanies;
